import ReactDOMServer from 'react-dom/server';
import HtmlToReact from 'html-to-react';
import queryString from 'query-string';
import { ReactElement } from 'react';
import CurrencyService from './currency-service';
import StructureService from './structure-service';
import ScreenService from './screen-service';
import {
  REGEXP_TEMPLATE_ALL_NON_FLOAT_SYMBOLS, REGEXP_TEMPLATE_ALL_NON_INTEGER_SYMBOLS, REGEXP_TEMPLATE_MULTIPLE_DOTS,
  REGEXP_TEMPLATE_MULTIPLE_MINUSES, REGEXP_TEMPLATE_MULTIPLE_DOTS_AND_COMMAS, REGEXP_TEMPLATE_COMMA_SEPARATED_NUMBER,
  REGEXP_TEMPLATE_CAMEL_CASE, REGEXP_TEMPLATE_HAS_DOT, REGEXP_TEMPLATE_CLEAR_FILE_NAME, REGEXP_TEMPLATE_LINK,
  REGEXP_TEMPLATE_ZERO_COUNTRY_CODE, REGEXP_TEMPLATE_EMAIL, REGEXP_TEMPLATE_PASSWORD, REGEXP_TEMPLATE_DOMAIN,
  REGEXP_TEMPLATE_MENTION_HTML_ANCHORS, REGEXP_TEMPLATE_HASHTAG_HTML_ANCHORS, REGEXP_TEMPLATE_SUBDOMAIN,
  REGEXP_TEMPLATE_LINK_PATTERN, REGEXP_TEMPLATE_FILE_NAME, REGEXP_TEMPLATE_ALL_NUMBER_SYMBOLS,
} from '../constants/system/regexp-templates';
import {
  KEYBOARD_KEY_ARROW_DOWN, KEYBOARD_KEY_ARROW_LEFT, KEYBOARD_KEY_ARROW_RIGHT, KEYBOARD_KEY_ARROW_UP,
  KEYBOARD_KEY_BACKSPACE, KEYBOARD_KEY_DOT, KEYBOARD_KEY_HYPHEN, KEYBOARD_KEY_TAB,
} from '../constants/system/keyboard-keys';
import { SIGN_COMMA, SIGN_DOT, SIGN_PLUS } from '../constants/system/signs';
import { HTML_TAG_NAME_DIV } from '../constants/system/html-tag-names';
import HTML_CHAR_BULLET from '../constants/system/html-chars';

export default class FormatService {
  public static TEXT_LIMIT_TABLE: number = 150;
  public static STRING_LIMIT_DEFAULT: number = 255;
  public static PERCENT_NUMBER_LIMIT: number = 100;
  public static FLOAT_NUMBER_PRECISION_DEFAULT: number = 2;
  public static FLOAT_NUMBER_PRECISION_SHORT: number = 1;
  public static INDEX_NOT_FOUND: number = -1;
  public static NUMBER_VALUE_DEFAULT: number = 0;
  public static VALUE_BILLION: number = 1000000000;
  public static VALUE_MILLION: number = 1000000;
  public static VALUE_THOUSAND: number = 1000;
  public static BOOLEAN_VALUE_FALSE: string = 'false';
  public static BOOLEAN_VALUE_TRUE: string = 'true';
  public static JSON_TYPE_CURRENCY: string = 'currency';
  public static JSON_TYPE_MEASURED: string = 'measured';
  public static JSON_TYPE_PERCENT: string = 'percent';
  public static JSON_TYPE_PRICING: string = 'pricing';

  private static BOOLEAN_LABEL_YES: string = 'Yes';
  private static BOOLEAN_LABEL_NO: string = 'No';
  private static NOTIFICATION_TEXT_LIMIT: number = 100;
  private static MIN_PHONE_LENGTH: number = 5;
  private static JSON_PRETTIFY_SPACE: number = 2;
  private static RADIX_DEFAULT: number = 10;

  public static formatCleanNumber(value: string, asFloat?: boolean, isPositive?: boolean): number {
    const result: string = this.formatNumberByTemplate(REGEXP_TEMPLATE_ALL_NON_FLOAT_SYMBOLS, value, isPositive);

    if (asFloat) {
      return result && !!this.parseFloat(result) ? this.parseFloat(result) : 0;
    }

    return result ? this.parseInt(result) : 0;
  }

  public static parseInt(value: string): number {
    return parseInt(value, this.RADIX_DEFAULT);
  }

  public static parseFloat(value: string): number {
    return parseFloat(value);
  }

  public static parseStringParam(value: string): string {
    return decodeURIComponent(value);
  }

  public static formatFixedFloatNumber(value: number): string {
    return value.toFixed(this.FLOAT_NUMBER_PRECISION_DEFAULT);
  }

  public static formatCleanNumberString(value: string): string {
    return this.formatNumberByTemplate(REGEXP_TEMPLATE_ALL_NON_FLOAT_SYMBOLS, value);
  }

  public static formatCleanIntegerNumberString(value: string): string {
    return this.formatNumberByTemplate(REGEXP_TEMPLATE_ALL_NON_INTEGER_SYMBOLS, value);
  }

  public static formatNumber(
    value?: string,
    isFloat?: boolean,
    limit?: number,
    isPositive?: boolean,
    withNoCommas?: boolean,
  ): string {
    if (!value) {
      return '';
    }

    let resultValue: string = value;

    resultValue = resultValue.toString().replace(REGEXP_TEMPLATE_MULTIPLE_DOTS, SIGN_DOT);

    if (limit) {
      const cleanNumber: number = this.formatCleanNumber(resultValue, isFloat, isPositive);

      resultValue = cleanNumber > limit ? limit.toString() : resultValue;
    }

    if (isPositive) {
      resultValue = resultValue.replace(REGEXP_TEMPLATE_MULTIPLE_MINUSES, '');
    }

    const withDot: boolean = resultValue.indexOf(SIGN_DOT) !== FormatService.INDEX_NOT_FOUND;
    if (withDot) {
      const decimalFirstPosition: number = resultValue.indexOf(SIGN_DOT);

      const leftSide: string = withNoCommas
        ? this.formatCleanNumberString(resultValue.substring(0, decimalFirstPosition))
        : this.formatSeparatedNumber(resultValue.substring(0, decimalFirstPosition));
      const rightSide: string = this.formatCleanNumberString(
        resultValue.substring(decimalFirstPosition + 1)
          .substring(0, this.FLOAT_NUMBER_PRECISION_DEFAULT)
          .replace(REGEXP_TEMPLATE_MULTIPLE_DOTS_AND_COMMAS, ''),
      );

      resultValue = isFloat ? `${leftSide}${SIGN_DOT}${rightSide}` : leftSide;
    } else {
      resultValue = withNoCommas ? this.formatCleanNumberString(resultValue) : this.formatSeparatedNumber(resultValue);
    }

    return resultValue;
  }

  public static formatStringAddress(streetAddress?: string, location?: string, postalCode?: string): string {
    return [streetAddress, location, postalCode]
      .filter((addressFragment: string | undefined): boolean => !!addressFragment).join(', ');
  }

  public static formatCamelCaseToKebabCase(value: string): string {
    return value.replace(REGEXP_TEMPLATE_CAMEL_CASE, '$1-$2').toLowerCase();
  }

  public static formatCamelCaseToWords(value: string): string {
    return this.formatStringCapitalized(value).replace(REGEXP_TEMPLATE_CAMEL_CASE, '$1 $2');
  }

  public static formatStringCapitalized(value: string): string {
    return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
  }

  public static formatCleanFileName(fileName: string): string {
    const templateHasDot: RegExp = new RegExp(REGEXP_TEMPLATE_HAS_DOT);

    return templateHasDot.exec(fileName) ? fileName.replace(REGEXP_TEMPLATE_CLEAR_FILE_NAME, '') : '';
  }

  public static formatLimitedString(value: string, limit: number): string {
    return value && value.length > limit ? `${value.substring(0, limit)}...` : value;
  }

  public static formatBooleanValue(value?: boolean, isNegativeBlank?: boolean): string {
    const negativeLabel: string = isNegativeBlank ? '' : this.BOOLEAN_LABEL_NO;

    return value ? this.BOOLEAN_LABEL_YES : negativeLabel;
  }

  public static formatRoundedBigNumber(value: string, precision?: number): string {
    const resultPrecision: number = precision || this.FLOAT_NUMBER_PRECISION_DEFAULT;
    const defaultValue: string = this.NUMBER_VALUE_DEFAULT.toString();

    if (!value) {
      return defaultValue;
    }

    const preparedValue: number = Math.abs(Number(value));
    if (!preparedValue) {
      return defaultValue;
    }

    const letterValueMap: Record<string, number> = {
      B: this.VALUE_BILLION,
      M: this.VALUE_MILLION,
      K: this.VALUE_THOUSAND,
    };

    for (let letterIndex: number = 0; letterIndex < Object.keys(letterValueMap).length; letterIndex++) {
      const letter: string = Object.keys(letterValueMap)[letterIndex];
      const valueThreshold: number = letterValueMap[letter];

      if (preparedValue >= valueThreshold) {
        return `${this.parseFloat((preparedValue / valueThreshold).toFixed(resultPrecision)).toString()}${letter}`;
      }
    }

    return `${preparedValue}`;
  }

  public static formatHtmlToComponent(htmlText: string): string | null {
    if (!htmlText) {
      return null;
    }

    const htmlToReactParser: HtmlToReactParser = HtmlToReact.Parser();

    return htmlToReactParser.parse(htmlText);
  }

  public static formatComponentToHtml(reactComponent: ReactElement): string | null {
    if (!reactComponent) {
      return null;
    }

    return ReactDOMServer.renderToStaticMarkup(reactComponent);
  }

  public static formatTextWithNoMentions(text: string): string {
    return text.replace(REGEXP_TEMPLATE_MENTION_HTML_ANCHORS, '$1');
  }

  public static formatTextWithNoHashtags(text: string): string {
    return text.replace(REGEXP_TEMPLATE_HASHTAG_HTML_ANCHORS, '$1');
  }

  public static formatLinkedText(
    text: string,
    links: Array<FormatLinkItem>,
    isActive: boolean,
    extractLink: boolean,
    asText: boolean,
    renderLinkCallback: Function,
  ): string | string[] {
    let result: string[] = [];
    let isLinkExtracted: boolean = false;

    if (links && links.length) {
      result = text.split(REGEXP_TEMPLATE_LINK_PATTERN);

      if (extractLink && StructureService.checkIsValueArray(result) && result[0]) {
        result[0] = this.formatNotificationLimitedText(result[0]);
      }

      for (let textIndex: number = 1; textIndex < result.length; textIndex++) {
        if (extractLink && isLinkExtracted) {
          return result.slice(0, textIndex);
        }

        for (let linkIndex: number = 0; linkIndex < links.length; linkIndex++) {
          const link: FormatLinkItem = links[linkIndex];

          if (result[textIndex] === this.formatLink(link.id)) {
            const linkText: string = extractLink ? 'Link' : link.name;

            if (renderLinkCallback) {
              result[textIndex] = renderLinkCallback(link, linkText, isActive);
            }

            isLinkExtracted = true;
          }
        }
      }
    }

    if (extractLink && !result.length) {
      return this.formatNotificationLimitedText(text);
    }

    if (asText) {
      return result.join('');
    }

    return result;
  }

  public static formatLink(linkId: number): string {
    return `{{ link-${linkId} }}`;
  }

  public static formatParsedDate(dateString?: string): Date | null {
    return dateString ? new Date(dateString) : null;
  }

  public static formatDateString(dateObject: Date): string {
    const monthValue: number = dateObject.getMonth() + 1;
    const dayValue: number = dateObject.getDate();
    const yearValue: number = dateObject.getFullYear();

    return `${monthValue < 10 ? `0${monthValue}` : monthValue}/${dayValue < 10 ? `0${dayValue}` : dayValue}/`
      + `${yearValue}`;
  }

  public static formatPhoneValue(phoneValue: string | number): string {
    return !phoneValue.toString().startsWith(SIGN_PLUS) ? `${SIGN_PLUS}${phoneValue}` : phoneValue.toString();
  }

  public static formatJsonString(object: JsonObject, isPrettified?: boolean): string | null {
    if (!object) {
      return null;
    }

    return isPrettified ? JSON.stringify(object, null, this.JSON_PRETTIFY_SPACE) : JSON.stringify(object);
  }

  public static formatFileName(fileName: string): string | null {
    return fileName ? fileName.match(REGEXP_TEMPLATE_FILE_NAME)?.toString() || null : null;
  }

  public static formatClassNames(...classNames: (string | undefined)[]): string {
    return classNames.filter((className: string | undefined): boolean => !!className).join(' ');
  }

  public static formatEncryptedString(string: string | null, length?: number): string | null {
    return string || length ? new Array(string ? string.length + 1 : length).join(HTML_CHAR_BULLET) : null;
  }

  public static formatCurrencyText(
    jsonObject: JsonObject, asFloat?: boolean, withEmptyValues?: boolean,
  ): string | null {
    if (!jsonObject || !jsonObject.type || jsonObject.type !== this.JSON_TYPE_CURRENCY || !jsonObject.currency) {
      return null;
    }

    if ((!withEmptyValues && (!jsonObject.value || this.parseFloat(jsonObject.value) === 0.00))
      || (withEmptyValues && (jsonObject.value === null || jsonObject.value === undefined))) {
      return null;
    }

    const currencySign: string | null = CurrencyService.getCurrencySign(jsonObject.currency);
    if (!currencySign) {
      return null;
    }

    return this.toCurrencyValue(jsonObject.value, currencySign, asFloat);
  }

  public static toCurrencyValue(
    value: string | number | undefined, currencySign: string, asFloat?: boolean,
  ): string | null {
    if (value || value === 0) {
      const formattedValue: string | null = this.toNumberWithCommas(value as string | number, asFloat);

      return `${currencySign}${formattedValue || 0}`;
    }

    return null;
  }

  public static toNumberWithCommas(value: string | number, asFloat?: boolean): string {
    const intValue: number = this.parseInt(value.toString());
    const floatValue: number = this.parseFloat(value.toString());
    const intStringValue: string = intValue.toString();
    const floatStringValue: string = floatValue > intValue ? this.formatFixedFloatNumber(floatValue) : intStringValue;
    const resultValue: string = asFloat ? floatStringValue : intStringValue;

    return resultValue && (value || value === 0) ? this.formatSeparatedNumber(resultValue) : '';
  }

  public static formatPercentText(jsonObject: JsonObject, withEmptyValues?: boolean): string | null {
    if (!jsonObject || !jsonObject.type || jsonObject.type !== this.JSON_TYPE_PERCENT) {
      return null;
    }

    if ((!withEmptyValues && (!jsonObject.value || this.parseFloat(jsonObject.value) === 0.00))
      || (withEmptyValues && (jsonObject.value === null || jsonObject.value === undefined))) {
      return null;
    }

    return this.toPercentValue(jsonObject.value);
  }

  public static toPercentValue(value: string | number | undefined): string | null {
    return value || value === 0 ? `${this.formatFixedFloatNumber(this.parseFloat(value.toString()))}%` : null;
  }

  public static formatPricingText(jsonObject: JsonObject, withEmptyValues?: boolean): string | null {
    if (!jsonObject || !jsonObject.type || jsonObject.type !== this.JSON_TYPE_PRICING) {
      return null;
    }

    if ((!withEmptyValues && (!jsonObject.value || this.parseFloat(jsonObject.value) === 0.00))
      || (withEmptyValues && (jsonObject.value === null || jsonObject.value === undefined))) {
      return null;
    }

    return this.toPercentValue(jsonObject.value);
  }

  public static formatMeasuredText(jsonObject: JsonObject, withEmptyValues?: boolean): string | null {
    if (!jsonObject || !jsonObject.type || jsonObject.type !== this.JSON_TYPE_MEASURED || !jsonObject.measureUnit) {
      return null;
    }

    if ((!withEmptyValues && (!jsonObject.value || this.parseFloat(jsonObject.value) === 0.00))
      || (withEmptyValues && (jsonObject.value === null || jsonObject.value === undefined))) {
      return null;
    }

    return this.toNumberWithCommas(jsonObject.value);
  }

  public static formatQueryParams(queryParams?: ParsedQueryParams | null): string {
    return queryParams ? `?${queryString.stringify(queryParams)}` : '';
  }

  public static parseQueryParams(queryParamsString?: string | null): ParsedQueryParams | null {
    return queryParamsString ? queryString.parse(queryParamsString) : null;
  }

  public static processNumberInput(
    numberInput: HTMLInputElement, isFloat: boolean, withNoCommas: boolean, withNegativeValues: boolean, limit?: number,
  ): string | null {
    const preActionSelectionStart: number | null = numberInput.selectionStart;
    const preActionSelectionEnd: number | null = numberInput.selectionEnd;
    const preActionLength: number = numberInput.value.length;
    const newValue: string = numberInput.value;

    numberInput.value = FormatService.formatNumber(newValue, isFloat, limit, !withNegativeValues, withNoCommas);

    const lengthDiff: number = numberInput.value.length - preActionLength;

    numberInput.selectionStart = (preActionSelectionStart || 0) + lengthDiff;
    numberInput.selectionEnd = (preActionSelectionEnd || 0) + lengthDiff;

    return newValue ? FormatService.formatNumber(newValue, isFloat, limit, !withNegativeValues, withNoCommas) : null;
  }

  public static shortenTextBySymbol(text: string, symbol: string): string {
    const symbolIndex: number = text.indexOf(symbol);

    if (symbolIndex === this.INDEX_NOT_FOUND) {
      return text;
    }

    return text.substring(0, symbolIndex).trim();
  }

  public static isSystemKey(key: string, isFloat?: boolean, withNegativeValues?: boolean): boolean {
    const systemKeyList: string[] = [
      KEYBOARD_KEY_ARROW_LEFT,
      KEYBOARD_KEY_ARROW_RIGHT,
      KEYBOARD_KEY_ARROW_DOWN,
      KEYBOARD_KEY_ARROW_UP,
      KEYBOARD_KEY_BACKSPACE,
      KEYBOARD_KEY_TAB,
      ...(isFloat ? [KEYBOARD_KEY_DOT] : []),
      ...(withNegativeValues ? [KEYBOARD_KEY_HYPHEN] : []),
    ];

    return systemKeyList.includes(key);
  }

  public static isNumberKey(key: string): boolean {
    return !!(new RegExp(REGEXP_TEMPLATE_ALL_NUMBER_SYMBOLS)).exec(key);
  }

  public static isValidLink(value: string): boolean {
    return (new RegExp(REGEXP_TEMPLATE_LINK)).test(value);
  }

  public static isValidDomain(value: string): boolean {
    return (new RegExp(REGEXP_TEMPLATE_DOMAIN)).test(value);
  }

  public static isValidSubdomain(value: string): boolean {
    return (new RegExp(REGEXP_TEMPLATE_SUBDOMAIN)).test(value);
  }

  public static isValidPassword(value: string): boolean {
    return (new RegExp(REGEXP_TEMPLATE_PASSWORD)).test(value);
  }

  public static isValidEmail(value: string): boolean {
    return (new RegExp(REGEXP_TEMPLATE_EMAIL)).test(value);
  }

  public static isValidPhoneNumber(value: string, asFilter?: boolean): boolean {
    return !!value && !(new RegExp(REGEXP_TEMPLATE_ZERO_COUNTRY_CODE)).test(value)
      && ((asFilter && value !== SIGN_PLUS && value.length > this.MIN_PHONE_LENGTH) || !asFilter);
  }

  public static isValidJson(jsonObject: string): boolean {
    try {
      return !!JSON.parse(jsonObject);
    } catch (exception) {
      return false;
    }
  }

  public static extractJson(value: string | null): JsonObject | JsonArray | null {
    if (value && this.isValidJson(value)) {
      return JSON.parse(value);
    }

    return null;
  }

  public static extractBooleanValue(value: string): boolean {
    return value ? Boolean(value.toString().replace(this.BOOLEAN_VALUE_FALSE, '')) : false;
  }

  public static extractHtmlAttributes(
    value: string,
    tagName: string,
    attributeName: string,
    asInteger: boolean,
  ): string[] | number[] {
    const element: HTMLDivElement | null = ScreenService.createElement(HTML_TAG_NAME_DIV) as HTMLDivElement;

    if (element) {
      element.innerHTML = value;
    }

    const elements: NodeListOf<HTMLElement> = ScreenService.getChildElementsByTagName(element, tagName);

    const attributes: string[] | number[] = elements && elements.length
      ? [...elements].map((element: HTMLElement): string | number | null => {
        const elementAttributes: NamedNodeMap = element.attributes;

        if (elementAttributes) {
          const elementAttribute: Attr | null = elementAttributes.getNamedItem(attributeName);

          if (!elementAttribute || !elementAttribute.nodeValue) {
            return null;
          }

          if (asInteger) {
            return this.parseInt(elementAttribute.nodeValue);
          }

          return elementAttribute.nodeValue;
        }

        return null;
      }).filter((attribute: string | number | null): boolean => !!attribute) as (string[] | number[]) : [];

    return asInteger ? [...new Set(attributes as number[])] : [...new Set(attributes as string[])];
  }

  public static extractWebsiteDomain(websiteUrl: string): string | null {
    let url: URL | null;

    try {
      url = new URL(websiteUrl);
    } catch (exception) {
      url = null;
    }

    if (!url) {
      return null;
    }

    return url.hostname || null;
  }

  private static formatSeparatedNumber(value: string): string {
    return value ? this.formatCleanNumberString(value).replace(REGEXP_TEMPLATE_COMMA_SEPARATED_NUMBER, SIGN_COMMA) : '';
  }

  private static formatNumberByTemplate(template: RegExp, value: string, isPositive?: boolean): string {
    let result: string = value ? value.toString().replace(template, '') : '';

    if (isPositive) {
      result = result.replace('-', '');
    }

    return result;
  }

  private static formatNotificationLimitedText(text: string): string {
    let resultText: string = text;

    if (resultText && resultText.length > this.NOTIFICATION_TEXT_LIMIT) {
      resultText = `${resultText && resultText.slice(0, this.NOTIFICATION_TEXT_LIMIT)} ... `;
    }

    return resultText;
  }
}
